import { type ContextEntry, getAll } from "./registry";

const SECRET_KEY_RE = /password|secret|token|apikey|api_key|key|credential/i;
const SECRET_VALUE_RE = /^[A-Za-z0-9+/]{32,}={0,2}$|^[0-9a-f]{32,}$/;

function scrubObject(obj: unknown, redactPaths: string[]): unknown {
  if (typeof obj !== "object" || obj === null) return obj;
  if (Array.isArray(obj)) return obj.map((item) => scrubObject(item, redactPaths));

  const result: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
    if (SECRET_KEY_RE.test(k)) {
      result[k] = "[REDACTED]";
    } else if (redactPaths.includes(k)) {
      result[k] = "[REDACTED]";
    } else if (typeof v === "string" && SECRET_VALUE_RE.test(v)) {
      result[k] = "[REDACTED]";
    } else {
      result[k] = scrubObject(v, redactPaths);
    }
  }
  return result;
}

function formatValue(entry: ContextEntry): string {
  const scrubbed = scrubObject(entry.value, entry.redact ?? []);
  if (typeof scrubbed === "string") return scrubbed;
  return JSON.stringify(scrubbed, null, 2);
}

function estimateTokens(text: string): number {
  return Math.ceil(text.length / 4);
}

/**
 * Builds a markdown context document from all registered entries.
 * Entries are sorted by priority desc, then registeredAt desc.
 * Lowest-priority entries are dropped first when maxTokens would be exceeded.
 */
export function buildContextDocument(maxTokens = 1500): string {
  const entries = getAll().sort((a, b) => {
    if (b.priority !== a.priority) return b.priority - a.priority;
    return b.registeredAt - a.registeredAt;
  });

  const lines: string[] = ["## Current context"];
  let usedTokens = estimateTokens(lines[0] ?? "");

  for (const entry of entries) {
    const value = formatValue(entry);
    const line = `- **${entry.label}**: ${value}`;
    const cost = estimateTokens(line) + 1; // +1 for newline
    if (usedTokens + cost > maxTokens) break;
    lines.push(line);
    usedTokens += cost;
  }

  return lines.join("\n");
}
